import { Table, Tooltip, Typography } from '@statsbomb/kitbag-components';
import { TFunction } from 'i18next';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { trackObjectOpenCloseEvent } from '@/analytics/snowplow/analytics';
import { useCellValue } from '@/hooks/useCellValue';
import { NestedObject } from '@/types/object';
import { getMetricKeyFromColumnKey } from '@/utils/metrics';
import { getDataType } from '@/utils/table';

export const CellValueToDisplay = ({
  value,
  formatRule,
  columnKey,
  previewLinkParams,
  children,
  showAsteriskWithTooltip,
  showNullAsNa,
  t,
  testId,
  tooltipContent,
  dataPill,
  className,
}: {
  value: NestedObject[string];
  formatRule: string | undefined;
  columnKey: string;
  previewLinkParams?: string;
  children?: ReactNode;
  showAsteriskWithTooltip?: boolean;
  t?: TFunction;
  testId?: string;
  tooltipContent?: string;
  showNullAsNa?: boolean;
  dataPill?: { rank: number; total?: number; disabled?: boolean; label?: string };
  className?: string;
}) => {
  const dataType = getDataType(formatRule);
  const cellValue = useCellValue(value, formatRule, columnKey, t);
  const translationObject = useTranslation('table');
  const missingValue = showNullAsNa ? translationObject.t('na') : null;
  const cellValueToRender =
    previewLinkParams && cellValue !== null ? (
      <Typography
        as={Link}
        variant="linkMedium"
        className="relative"
        to={`./preview?${previewLinkParams}`}
        onClick={async () => {
          await trackObjectOpenCloseEvent({
            objectType: 'Widget',
            openVsClose: 'Open',
            conditionType: getMetricKeyFromColumnKey(columnKey),
          });
        }}
      >
        {cellValue}
      </Typography>
    ) : (
      ((cellValue || missingValue) as string | React.JSX.Element)
    );

  const valueWithPossibleTooltip = tooltipContent ? (
    <Tooltip content={tooltipContent}>
      {cellValueToRender}
      {showAsteriskWithTooltip ? <span className="absolute">*</span> : null}
    </Tooltip>
  ) : (
    cellValueToRender
  );

  return (
    <Table.DataCell alignVertically data-testid={testId} dataType={dataType} dataPill={dataPill} className={className}>
      {children}
      {valueWithPossibleTooltip}
    </Table.DataCell>
  );
};
